import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { NotificationSettingCategoryModel } from './models/notification-setting-category.model';
import { NotificationSettingItemModel } from './models/notification-setting-item.model';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

type NotificationChannel = 'inApp' | 'email';

@Component({
  selector: 'ca-notification-channel-matrix',
  templateUrl: './notification-channel-matrix.component.html',
  styleUrls: ['./notification-channel-matrix.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class NotificationChannelMatrixComponent implements OnChanges {
  @Input() categories: NotificationSettingCategoryModel[] = [];
  @Input() disabled = false;

  @Output() changed = new EventEmitter<void>();

  displayedColumns: string[] = ['expand', 'name', 'inApp', 'email'];
  detailDisplayedColumns: string[] = ['name', 'inApp', 'email'];

  dataSource = new MatTableDataSource<NotificationSettingCategoryModel>();

  filteredCategories: NotificationSettingCategoryModel[] = [];

  // Multiple categories can be open at the same time ("Expand all"), so the
  // expansion state is kept per category code instead of a single element.
  expanded: Record<string, boolean> = {};

  searchTerm = '';
  isSearching = false;
  hasResults = false;

  totalItemCountText = '0';
  filteredItemCountText = '0';

  // Expansion state as it was before the search took over, so it can be restored
  // once the search box is cleared again.
  private expandedSnapshot: Record<string, boolean> = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['categories']) {
      this.refresh();
    }
  }

  trackByCategoryCode(index: number, category: NotificationSettingCategoryModel): string {
    return category.code;
  }

  trackByNotificationType(index: number, item: NotificationSettingItemModel): string {
    return item.notificationType;
  }

  getCategoryLabel(category: NotificationSettingCategoryModel): string {
    return category.displayName + ' (' + (category.items?.length ?? 0) + ')';
  }

  toggleCategory(category: NotificationSettingCategoryModel): void {
    this.expanded = { ...this.expanded, [category.code]: !this.expanded[category.code] };
  }

  expandAll(): void {
    const next: Record<string, boolean> = { ...this.expanded };
    this.filteredCategories.forEach(category => (next[category.code] = true));
    this.expanded = next;
  }

  collapseAll(): void {
    this.expanded = {};
  }

  onSearchInput(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value ?? '';
    this.refresh();
  }

  onToggle(
    item: NotificationSettingItemModel,
    channel: NotificationChannel,
    event: MatSlideToggleChange,
  ): void {
    const checked = event.checked;

    if (channel === 'inApp') {
      item.inAppEnabled = checked;
    } else {
      item.emailEnabled = checked;
    }

    this.changed.emit();
  }

  private refresh(): void {
    const wasSearching = this.isSearching;
    this.isSearching = this.normalize(this.searchTerm).length > 0;

    this.filteredCategories = this.buildFilteredCategories();
    this.dataSource.data = this.filteredCategories;

    this.hasResults = this.filteredCategories.length > 0;
    this.totalItemCountText = String(this.countItems(this.categories));
    this.filteredItemCountText = String(this.countItems(this.filteredCategories));

    this.applySearchExpansion(wasSearching);
  }

  private buildFilteredCategories(): NotificationSettingCategoryModel[] {
    const categories = this.categories ?? [];

    if (!this.isSearching) {
      return categories;
    }

    const term = this.normalize(this.searchTerm);
    const result: NotificationSettingCategoryModel[] = [];

    categories.forEach(category => {
      const items = (category.items ?? []).filter(item => this.matches(item, term));

      if (items.length > 0) {
        // The item instances are reused on purpose so that toggling still mutates
        // the objects owned by the host page.
        const filtered = new NotificationSettingCategoryModel();
        filtered.code = category.code;
        filtered.displayName = category.displayName;
        filtered.items = items;
        result.push(filtered);
      }
    });

    return result;
  }

  private applySearchExpansion(wasSearching: boolean): void {
    if (this.isSearching) {
      if (!wasSearching) {
        this.expandedSnapshot = { ...this.expanded };
      }

      const next: Record<string, boolean> = {};
      this.filteredCategories.forEach(category => (next[category.code] = true));
      this.expanded = next;

      return;
    }

    if (wasSearching && this.expandedSnapshot) {
      this.expanded = this.expandedSnapshot;
      this.expandedSnapshot = null;
    }
  }

  private matches(item: NotificationSettingItemModel, term: string): boolean {
    return (
      this.normalize(item.displayName).includes(term) ||
      this.normalize(item.description).includes(term)
    );
  }

  private countItems(categories: NotificationSettingCategoryModel[]): number {
    return (categories ?? []).reduce((total, category) => total + (category.items?.length ?? 0), 0);
  }

  // Turkish locale lowercasing so that "I/ı" and "İ/i" pairs match as expected.
  private normalize(value: string): string {
    return (value ?? '').toLocaleLowerCase('tr').trim();
  }
}
